import { useReducer } from 'react'

import { useController, useFormContext } from 'react-hook-form'

import EditIcon from '@/assets/icons/icon-edit.svg'
import { Toggle } from '@/components/ui/Toggle'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { useToast } from '@/components/ui/toast.tsx'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'

import { useDisplaySettingsMutations } from '../hooks/use-display-settings-mutations'

const initialState = {
  isPresetDialogOpen: false,
  isPresetDialogClosing: false,
}

type StateType = typeof initialState

const reducer = (state: StateType, action: Partial<StateType>) =>
  Object.assign({}, state, action)

export const DisplaySettingsCustomNotePreset = () => {
  const toast = useToast()
  const { control } = useFormContext()
  const [{ isPresetDialogClosing, isPresetDialogOpen }, dispatch] = useReducer(
    reducer,
    initialState,
  )

  const { setSettingsData, customNotePreset, customNotePresetEnabled } =
    useSettingsContext()

  const { field: customNotePresetField } = useController({
    control,
    name: 'customNotePreset',
  })

  const { field: customNotePresetEnabledField } = useController({
    control,
    name: 'customNotePresetEnabled',
  })

  const { enableCustomPresetMutation, setCustomPresetMutation } =
    useDisplaySettingsMutations()

  const handleChangeEnabled = (enabled: boolean) => {
    return enableCustomPresetMutation.mutateAsync(enabled).then(() => {
      setSettingsData((p) => ({
        ...p,
        customNotePresetEnabled: enabled,
      }))

      toast.success(
        enabled
          ? 'Successfully enabled Custom Note Preset'
          : 'Successfully disabled Custom Note Preset',
      )

      if (enabled) {
        dispatch({ isPresetDialogOpen: true })
      }
    })
  }

  const handlePresetDialogClose = async () => {
    if (!customNotePresetField.value) {
      dispatch({ isPresetDialogClosing: true })
      await handleChangeEnabled(false)
      dispatch({ isPresetDialogClosing: false, isPresetDialogOpen: false })
    } else {
      dispatch({ isPresetDialogOpen: false })
    }
  }

  const handleChangePreset = () => {
    return setCustomPresetMutation
      .mutateAsync(customNotePresetField.value)
      .then(() => {
        setSettingsData((p) => ({
          ...p,
          customNotePreset: customNotePresetField.value,
        }))
        toast.success('Successfully changed Custom Note Preset')
        handlePresetDialogClose()
      })
  }

  return (
    <SettingsOption
      title="Custom Note Preset"
      mobileVertical
      description="Set note presets that automatically fill in empty fan notes"
    >
      <div className="flex items-center gap-4">
        <p className="text-title text-sm font-medium">Activate</p>

        <Toggle
          name={customNotePresetEnabledField.name}
          checked={customNotePresetEnabledField.value}
          onChange={({ target: { checked } }) => handleChangeEnabled(checked)}
        />
        {customNotePresetEnabled && !!customNotePreset && (
          <EditIcon
            type="button"
            className="text-primary-500 cursor-pointer"
            onClick={() => dispatch({ isPresetDialogOpen: true })}
            width={18}
            height={18}
          />
        )}
      </div>

      <Dialog
        show={isPresetDialogOpen}
        onClose={handlePresetDialogClose}
        bigDialog
        showCloseIcon={false}
        panelClassName="sm:p-8"
      >
        <Dialog.Header>
          <Dialog.Title className="text-3xl">Custom Note Preset</Dialog.Title>
        </Dialog.Header>
        <Dialog.Description className="mt-2 block text-center">
          Allows users to set note presets that automatically fill in empty fan
          notes
        </Dialog.Description>
        <Dialog.Description>
          <div className="mt-6 flex flex-col gap-1.5">
            <label
              htmlFor="detail-field"
              className="text-title text-sm font-medium"
            >
              Note
            </label>
            <textarea
              rows={5}
              className="bg-card text-title focus:border-primary-500 focus:outline-primary-500 w-full rounded-md border px-3 py-2 focus:outline-0"
              placeholder="Type a note..."
              {...customNotePresetField}
            />
          </div>
        </Dialog.Description>
        <Dialog.ClosableFooter
          isLoading={isPresetDialogClosing}
          onClose={handlePresetDialogClose}
          disabled={setCustomPresetMutation.isLoading}
        >
          <Button
            disabled={!customNotePresetField.value}
            onClick={handleChangePreset}
            isLoading={setCustomPresetMutation.isLoading}
          >
            Save
          </Button>
        </Dialog.ClosableFooter>
      </Dialog>
    </SettingsOption>
  )
}
